import React, { useState, useMemo } from 'react';
import { useAppData } from '@/context/AppContext';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import { Plus, Trash2, Edit2, Tag } from 'lucide-react';
import styles from './NotesPage.module.css';

const CATEGORIES = ['General', 'Pain', 'Exercise', 'Nutrition', 'Mental', 'Appointment'];

const CATEGORY_COLORS: Record<string, string> = {
  General: 'var(--text-secondary)',
  Pain: 'var(--primary)',
  Exercise: 'var(--secondary)',
  Nutrition: 'var(--success)',
  Mental: '#9C27B0',
  Appointment: 'var(--tertiary)',
};

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

export default function NotesPage() {
  const { userNotes, addNote, updateNote, deleteNote } = useAppData();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<any>(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'General' });
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredNotes = useMemo(() => {
    const sorted = [...userNotes].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    if (!filterCategory) return sorted;
    return sorted.filter(n => n.category === filterCategory);
  }, [userNotes, filterCategory]);

  const openAdd = () => {
    setEditingNote(null);
    setForm({ title: '', content: '', category: 'General' });
    setModalOpen(true);
  };

  const openEdit = (note: any) => {
    setEditingNote(note);
    setForm({ title: note.title || '', content: note.content || '', category: note.category || 'General' });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.content.trim()) return;
    setSaving(true);
    try {
      if (editingNote) {
        await updateNote(editingNote.id, { title: form.title.trim(), content: form.content.trim(), category: form.category });
      } else {
        await addNote({ title: form.title.trim(), content: form.content.trim(), category: form.category });
      }
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteNote(id);
    setConfirmDeleteId(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Journal</h1>
        <button className={styles.addBtn} onClick={openAdd}>
          <Plus size={16} /> Add Note
        </button>
      </div>

      <div className={styles.filterRow}>
        <button
          className={`${styles.filterChip} ${filterCategory === null ? styles.filterChipActive : ''}`}
          onClick={() => setFilterCategory(null)}
        >
          All
        </button>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`${styles.filterChip} ${filterCategory === cat ? styles.filterChipActive : ''}`}
            onClick={() => setFilterCategory(cat === filterCategory ? null : cat)}
            style={filterCategory === cat ? { borderColor: CATEGORY_COLORS[cat], color: CATEGORY_COLORS[cat], background: CATEGORY_COLORS[cat] + '18' } : {}}
          >
            {cat}
          </button>
        ))}
      </div>

      {filteredNotes.length === 0 ? (
        <Card>
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <Edit2 size={32} />
            </div>
            <div className={styles.emptyTitle}>
              {filterCategory ? `No ${filterCategory} notes yet` : 'No journal entries yet'}
            </div>
            <div className={styles.emptyText}>
              Record your recovery journey — how you're feeling, what's helping, things to discuss with your physio.
            </div>
            <button className={styles.addBtn} onClick={openAdd}>
              <Plus size={14} /> Write your first entry
            </button>
          </div>
        </Card>
      ) : (
        <div className={styles.notesList}>
          {filteredNotes.map((note: any) => {
            const ts = note.timestamp instanceof Date ? note.timestamp : new Date(note.timestamp);
            const catColor = CATEGORY_COLORS[note.category] || 'var(--text-secondary)';
            return (
              <Card key={note.id} className={styles.noteCard}>
                <div className={styles.noteHeader}>
                  <div className={styles.noteMeta}>
                    <span className={styles.noteCategory} style={{ color: catColor, background: catColor + '18' }}>
                      <Tag size={10} />
                      {note.category || 'General'}
                    </span>
                    <span className={styles.noteDate}>{formatDate(ts)} at {formatTime(ts)}</span>
                  </div>
                  <div className={styles.noteActions}>
                    <button className={styles.iconBtn} onClick={() => openEdit(note)}>
                      <Edit2 size={14} />
                    </button>
                    <button
                      className={`${styles.iconBtn} ${styles.deleteBtn}`}
                      onClick={() => setConfirmDeleteId(note.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {note.title ? <div className={styles.noteTitle}>{note.title}</div> : null}
                <div className={styles.noteContent}>{note.content}</div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingNote ? 'Edit Note' : 'New Journal Entry'}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Title (optional)</label>
          <input
            className={styles.formInput}
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Week 3 check-in"
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Category</label>
          <div className={styles.categoryGrid}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                type="button"
                className={`${styles.categoryChip} ${form.category === cat ? styles.categoryChipActive : ''}`}
                style={form.category === cat ? { borderColor: CATEGORY_COLORS[cat], color: CATEGORY_COLORS[cat], background: CATEGORY_COLORS[cat] + '18' } : {}}
                onClick={() => setForm(f => ({ ...f, category: cat }))}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Note</label>
          <textarea
            className={styles.formTextarea}
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            placeholder="How are you feeling today? Any pain changes, wins, or things to discuss with your physio..."
            rows={7}
            autoFocus={!editingNote}
          />
        </div>
        <button
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={saving || !form.content.trim()}
        >
          {saving ? 'Saving...' : editingNote ? 'Update Note' : 'Save Entry'}
        </button>
      </Modal>

      <Modal isOpen={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)} title="Delete Note">
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          Are you sure you want to delete this journal entry? This cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px', fontWeight: 600, cursor: 'pointer', color: 'var(--text)' }}
            onClick={() => setConfirmDeleteId(null)}
          >
            Cancel
          </button>
          <button
            style={{ flex: 1, background: 'var(--error)', border: 'none', borderRadius: 8, padding: '12px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}
            onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
